/* Copyright (c) 2020-present CloudIO Inc */

import { isNotEmpty } from 'utils/isEmpty';
import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { page as SamplePage } from '../../help/sample_pages/SamplePageSimple';
import { baseFieldProps, fontStyleProps } from '../TextField/BaseField';
import { defaultValue } from './RadioBox';

const RadioBoxManager: ComponentManagerFn<'RadioBox'> = (
  platform: Platform,
): ComponentManager<'RadioBox'> => {
  return {
    ...baseComponentManager<'RadioBox'>(platform, 'RadioBox'),
    type: 'RadioBox',
    name: 'Radio Box',
    isChildAllowed: (page, item, childType) =>
      (
        ['TriggerOnChange', 'TriggerAfterServerValidate'] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      ...fontStyleProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => ['String', 'YN', 'TF', 'Boolean'],
      },
      color: {
        label: 'Color',
        type: 'ThemeBGColor',
        filterColor: (c) => !['ioGrey', 'paper', 'inherit'].includes(c),
      },
      options: {
        label: 'Options',
        type: 'options',
        category: 'Data',
      },
      serverValidateOnChange: {
        label: 'Server Validate On Change',
        type: 'boolean',
        category: 'Data',
      },
    },
    category: 'Forms',
    getDisplayText: (item) =>
      isNotEmpty(item.props.label.value)
        ? item.props.label.value
        : item.props.viewAttribute,
    icon: { iconName: 'dot-circle' },
    replaceableWith: ['Select', 'TextField', 'Checkbox'],
    onPropertyChange: (property, value, item, { appUid, pageId, itemId }) => {
      if (property === 'options' && typeof value === 'string') {
        const store = platform.getParentStore(appUid, pageId, itemId);
        const attr = store?.getAttribute(item.viewAttribute);
        if (attr?.attributeType === 'Boolean') {
          const options = value.split(',').map((o) => o.trim());
          const optionValues = options.map((o) =>
            o.split(':').map((o2) => o2.trim()),
          );
          const optionValuesOnly = optionValues
            .map((o) => o[0])
            .find(
              (o) =>
                o !== 'Y' &&
                o !== 'N' &&
                o !== 'T' &&
                o !== 'F' &&
                o !== 'true' &&
                o !== 'false',
            );
          if (optionValuesOnly) {
            platform.showError(
              `Invalid option value ${optionValuesOnly} for Boolean attribute ${item.viewAttribute}! Must be one of [true, false, Y, N, T or F].`,
            );
            return {};
          }
        }
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, itemData, { appUid, pageId, itemId }): boolean {
      switch (prop) {
        case 'colorExpression':
        case 'textItalic':
        case 'textBold':
        case 'textFontSize':
          return false;
        default:
          return true;
      }
    },
    onReplace: (item, fromItem) => {
      if (fromItem.type === 'Select') {
        item.props.options = fromItem.props.optionValues;
      } else if (fromItem.type === 'Checkbox') {
        item.props.options = { type: 'text', value: 'Y:Yes, N:No' };
      }
      return item;
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    description: `**Overview**<br/>
The Radio Box component allows users to select exactly one option from a predefined set. It renders as a list of circular buttons where selecting one automatically deselects the others. Unlike a dropdown (Select), all available options are visible at once, making it ideal for small lists where quick visibility is important.
Use this component for mutually exclusive choices, such as "Gender" (Male/Female), "Payment Method" (Credit/Debit/Paypal), or "Yes/No" questions.

**1. Data Binding**<br/>
This component binds to a field in your Data Store to save the selected value.
* **View Attribute:** Select the Data Store field to read from and write to.
* **Supported Types:** **String**, **Boolean**, **YN** (Yes/No), **TF** (True/False).

**2. Defining Options**<br/>
You must explicitly define the list of choices available to the user.
* **Options:** Accepts a comma-separated string defining the choices.
* **Simple List:** "Red, Green, Blue" (The value "Red" is displayed and saved).
* **Key:Value Pair:** "R:Red, G:Green, B:Blue" (The user sees "Red", but the system saves "R").
* **Validation:** If binding to a **Boolean** attribute, the system enforces that your option keys must be valid boolean representations (Y, N, T, F, true, false).

**3. Visual Styling**<br/>
* **Color:** Sets the color of the selected radio button (the dot inside the circle).
* **Constraint:** Neutral colors like **ioGrey** and **paper** are disabled to ensure the selection state is clearly visible.
* **Font Styles:** While standard text properties are supported, advanced styling like **Italic**, **Bold**, and **FontSize** controls are hidden by default to maintain UI consistency.

**4. Interactivity**<br/>
* **TriggerOnChange:** Supports the **TriggerOnChange** child component to execute logic immediately when a selection is made.
* **Server Validate On Change:** If enabled, the component validates the selection with the server instantly upon user interaction.

**5. Best Use Cases**<br/>
* **Binary Choices:** "Active / Inactive" status selectors.
* **Short Lists:** Selecting T-shirt sizes (S, M, L, XL).
* **Priority Setting:** Selecting "High / Medium / Low" urgency.
* **Questionnaires:** Answering "Yes / No" to survey questions.`,
    helpRenders: [
      {
        title: 'RadioBox',
        description: 'Simple Options',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: ['options'],
        item: {
          itemId: 'tf',
          props: {
            viewAttribute: 'userName',
            label: { type: 'text', value: 'Choose Color' },
            options: { type: 'text', value: 'Red, Green' },
          },
          children: [],
          type: 'RadioBox',
          parent: 'form',
        },
      },
      {
        title: 'RadioBox',
        description:
          'Options with Key Value Pairs. In the below example, values R or G will be stored in the database instead of Red or Green.',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: true,
        visibleProps: ['options'],
        item: {
          itemId: 'tf',
          props: {
            viewAttribute: 'userName',
            label: { type: 'text', value: 'Choose Color' },
            options: { type: 'text', value: 'R:Red, G:Green' },
          },
          children: [],
          type: 'RadioBox',
          parent: 'form',
        },
      },
      {
        title: 'Color',
        page: SamplePage,
        exampleId: 'e3',
        clearParentChildren: true,
        visibleProps: ['color'],
        item: {
          itemId: 'tf',
          props: {
            viewAttribute: 'userName',
            label: { type: 'text', value: 'Choose Color' },
            options: { type: 'text', value: 'Red, Green' },
            color: 'default',
          },
          children: [],
          type: 'RadioBox',
          parent: 'form',
        },
      },
    ],
  };
};

export default RadioBoxManager;
